import { useEffect, useState } from 'react'

import LabelAndPhoneInput from '@components/inputs/LabelAndPhoneInput'
import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import ButtonIconText from '@components/buttons/ButtonIconText'
import CheckAndLabel from '@components/check/CheckAndLabel'
import Title from '@components/title/Title'
import Pictogram from '@components/pictogram/Pictogram'
import person from '@assets/form/person.svg'
import deleteicon from '@assets/form/DeleteIcon.svg'
import corporation from '@assets/form/moral.svg'
import SelectBoxAndLabel from '@components/select/SelectBoxAndLabel'
import RadioGroupAndLabel from '@components/radio/RadioGroupAndLabel'
import { useAppDispatch, useAppSelector } from '@/hooks'
import {
	setAddress,
	setBirthCity,
	setBirthCountry,
	setBirthDate,
	setChildren,
	setContractDate,
	setContractPlace,
	setContractType,
	setEmail,
	setFirstName,
	setGender,
	setHasContract,
	setLastName,
	setMarriageCity,
	setMarriageDate,
	setMarried,
	setNationality,
	setPhone,
	setProfession,
} from './personSlice'
import {
	setCorporationAddress,
	setCorporationCapital,
	setCorporationDelegateAddress,
	setCorporationDelegateEmail,
	setCorporationDelegateFirstName,
	setCorporationDelegateGender,
	setCorporationDelegateLastName,
	setCorporationDelegatePhone,
	setCorporationName,
	setCorporationRegistar,
	setCorporationSiren,
} from './corporationSlice'
import type { RootState } from '@/store'
import type { Person as PersonData } from '@/api/form/v1/form_pb'
import { answerValue, type FieldRules } from '../fieldRules'
import {
	emailValidator,
	minLength2Validator,
	noNumberValidator,
	numberValidator,
	schemaValidator,
} from '@/utils/validators'

import './Section.css'

interface Section2Props {
	id: string
	position: number
	onRemoveBuyer: (id: string) => void
	buyerType: string
	onSetBuyerType: (value: string) => void
	// What the schema says about each field.
	rules: FieldRules
	// The answers the whole form shares, for the schema's conditions.
	answers: Record<string, string>
	buyerTypeOptions?: string[]
}

interface BuyerProps {
	id: string
	rules: FieldRules
	// The answers the schema's conditions read: the form's, and in the nested
	// parts, this buyer's own.
	answers: Record<string, string>
}

interface PersonProps extends BuyerProps {
	// The first buyer, whose household a co-buyer can share. Unset for the first
	// buyer themself.
	householdSourceId?: string
}

const MARITAL_STATUS_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner',
	single: 'Célibataire',
	married: 'Marié',
	civilUnion: 'Pacsé',
	divorced: 'Divorcé',
	widowhood: 'Veuf',
}

// What co-buyers living together share, and can take from the first buyer
// rather than type twice. Who they are, and how to reach them, stays their own.
const HOUSEHOLD_KEYS = [
	'address',
	'maritalStatus',
	'children',
	'marriageCity',
	'marriageDate',
	'hasContract',
	'contractDate',
	'contractPlace',
	'contractType',
]
type MarriageProps = BuyerProps
type ContractProps = BuyerProps

interface CorporationProps extends BuyerProps {
	position: number
}

function computeID(id: string, name: string): string {
	return `${id}-${name}`
}

// householdOf lists a person's household answers. The contract answer is the
// id of the radio picked, which names the buyer, so it is reduced to the option
// for two buyers to compare equal.
function householdOf(person: PersonData): string[] {
	return [
		person.address,
		person.married,
		person.children,
		person.marriageCity,
		person.marriageDate,
		answerValue(person.hasContract),
		person.contractDate,
		person.contractPlace,
		person.contractType,
	]
}

function sharesHousehold(a: PersonData, b: PersonData): boolean {
	const theirs = householdOf(b)
	return householdOf(a).every((value, index) => value === theirs[index])
}

// householdActions give the buyer `id` the household of `source`.
function householdActions(id: string, source: PersonData) {
	const hasContract = answerValue(source.hasContract)
	return [
		setAddress({ id, value: source.address }),
		setMarried({ id, value: source.married }),
		setChildren({ id, value: source.children }),
		setMarriageCity({ id, value: source.marriageCity }),
		setMarriageDate({ id, value: source.marriageDate }),
		// It has to point at this buyer's own radio, not the first buyer's.
		setHasContract({
			id,
			value: hasContract === '' ? '' : `${computeID(id, 'personHasContract')}-${hasContract}`,
		}),
		setContractDate({ id, value: source.contractDate }),
		setContractPlace({ id, value: source.contractPlace }),
		setContractType({ id, value: source.contractType }),
	]
}

// householdSummary is the one line that stands for the household fields while
// they follow the first buyer's.
function householdSummary(source: PersonData): string {
	const children = Number(source.children)
	return [
		source.address,
		source.married !== '' ? MARITAL_STATUS_OPTIONS[source.married] : '',
		source.children === '' ? '' : `${children} ${children > 1 ? 'enfants' : 'enfant'}`,
	]
		.filter(part => part !== '')
		.join(' · ')
}

function Section2(props: Section2Props) {
	const { rules } = props
	const buyerTypeOptions = props.buyerTypeOptions ?? []
	const getPersons = useAppSelector((state: RootState) => state.person.persons)

	const canChoosePerson = buyerTypeOptions.length === 0 || buyerTypeOptions.includes('person')
	const canChooseCorporation =
		buyerTypeOptions.length === 0 || buyerTypeOptions.includes('corporation')
	const hasVisibleCoordinates = rules.asks(
		'buyerType',
		'gender',
		'lastName',
		'firstName',
		'phone',
		'email',
		'birthDate',
		'birthCity',
		'birthCountry',
		'nationality',
		'profession',
		'address',
		'maritalStatus',
		'children',
		'marriageCity',
		'marriageDate',
		'hasContract',
		'contractDate',
		'contractPlace',
		'contractType',
		'corporationName',
		'corporationCapital',
		'corporationAddress',
		'corporationSiren',
		'corporationRegistar',
		'delegateGender',
		'delegateLastName',
		'delegateFirstName',
		'delegatePhone',
		'delegateEmail',
		'delegateAddress',
	)

	if (!hasVisibleCoordinates || (!canChoosePerson && !canChooseCorporation)) {
		return null
	}

	const handleRemoveBuyer = () => {
		props.onRemoveBuyer(props.id)
	}

	return (
		<div className='section'>
			<div className='headerSection'>
				<Title>Vos coordonnées</Title>
				{Object.keys(getPersons).length > 1 && (
					<ButtonIconText
						id='suppButton'
						icon={deleteicon}
						text='Supprimer ce co-acquéreur'
						onClick={handleRemoveBuyer}
					/>
				)}
			</div>
			{rules.asks('buyerType') && (
				<div className='pictograms'>
					{canChoosePerson && (props.position === 0 || props.buyerType === 'person') && (
						<Pictogram
							className={'picto1' + (props.buyerType === 'person' ? ' active' : '')}
							src={person}
							id='person'
							onChange={() => props.onSetBuyerType('person')}
							style={{ cursor: 'pointer' }}
						>
							Personne physique
						</Pictogram>
					)}
					{canChooseCorporation &&
						(props.position === 0 || props.buyerType === 'corporation') && (
							<Pictogram
								className={'picto1' + (props.buyerType === 'corporation' ? ' active' : '')}
								src={corporation}
								id='corporation'
								onChange={() => props.onSetBuyerType('corporation')}
								style={{ cursor: 'pointer' }}
							>
								Personne morale
							</Pictogram>
						)}
				</div>
			)}
			{props.buyerType === 'person' && (
				<Person
					id={props.id}
					rules={rules}
					answers={props.answers}
					householdSourceId={props.position > 0 ? getPersons[0]?.id : undefined}
				/>
			)}
			{props.buyerType === 'corporation' && (
				<Corporation
					id={props.id}
					position={props.position}
					rules={rules}
					answers={props.answers}
				/>
			)}
		</div>
	)
}

function Person(props: PersonProps) {
	const dispatch = useAppDispatch()
	const { rules } = props

	const getGender = useAppSelector(
		(state: RootState) => state.person.persons.find(person => props.id === person.id)?.gender ?? '',
	)
	const getLastName = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.lastName ?? '',
	)
	const getFirstName = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.firstName ?? '',
	)
	const getPhone = useAppSelector(
		(state: RootState) => state.person.persons.find(person => props.id === person.id)?.phone ?? '',
	)
	const getEmail = useAppSelector(
		(state: RootState) => state.person.persons.find(person => props.id === person.id)?.email ?? '',
	)
	const getBirthDate = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.birthDate ?? '',
	)
	const getBirthCity = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.birthCity ?? '',
	)
	const getBirthCountry = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.birthCountry ?? '',
	)
	const getNationality = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.nationality ?? '',
	)
	const getProfession = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.profession ?? '',
	)
	const getAddress = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.address ?? '',
	)
	const getMarried = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.married ?? '',
	)
	const getChildren = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.children ?? '',
	)
	const getHasContract = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.hasContract ?? '',
	)
	const self = useAppSelector((state: RootState) =>
		state.person.persons.find(person => props.id === person.id),
	)
	const householdSource = useAppSelector((state: RootState) =>
		state.person.persons.find(person => props.householdSourceId === person.id),
	)

	// A co-buyer reloaded with the first buyer's household was sharing it.
	const [sameHousehold, setSameHousehold] = useState(
		() =>
			self !== undefined &&
			householdSource !== undefined &&
			householdOf(householdSource).some(value => value !== '') &&
			sharesHousehold(self, householdSource),
	)
	// Only a co-buyer has a first buyer to share with: should this one become
	// the first, its fields show again.
	const linked = sameHousehold && householdSource !== undefined

	// While shared, the household follows the first buyer's, edits included.
	useEffect(() => {
		if (!linked || !self || !householdSource || sharesHousehold(self, householdSource)) return
		householdActions(props.id, householdSource).forEach(action => dispatch(action))
	}, [linked, self, householdSource, props.id, dispatch])

	// This buyer's answers, for the schema's conditions on their questions.
	const answers = {
		...props.answers,
		gender: getGender,
		lastName: getLastName,
		firstName: getFirstName,
		phone: getPhone,
		email: getEmail,
		birthDate: getBirthDate,
		birthCity: getBirthCity,
		birthCountry: getBirthCountry,
		nationality: getNationality,
		profession: getProfession,
		address: getAddress,
		maritalStatus: getMarried,
		children: getChildren,
		hasContract: getHasContract,
	}
	// Whether the schema shows any of these questions for the answers so far.
	const shows = (...keys: string[]) => keys.some(key => rules.shows(key, answers))

	return (
		<div>
			<div className='formContent'>
				{shows('gender') && (
					<div className='fitContentLine'>
						<SelectBoxAndLabel
							id={computeID(props.id, 'personGender')}
							text='Civilité'
							options={{ '': 'Sélectionner', mr: 'M', mrs: 'Mme' }}
							value={getGender}
							validator={schemaValidator(rules.requires('gender'))}
							onChange={value => dispatch(setGender({ id: props.id, value }))}
						/>
					</div>
				)}
				{shows('lastName', 'firstName') && (
					<div className='fieldLine'>
						{shows('lastName') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personLastname')}
								label='Nom'
								name='lastName'
								autocomplete='family-name'
								placeholder='Ex : Dupont'
								value={getLastName}
								validator={schemaValidator(rules.requires('lastName'), minLength2Validator, noNumberValidator)}
								onChange={value => dispatch(setLastName({ id: props.id, value }))}
							/>
						)}
						{shows('firstName') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personFirstname')}
								label='Prénom'
								name='firstName'
								autocomplete='given-name'
								placeholder='Ex : Jean'
								value={getFirstName}
								validator={schemaValidator(rules.requires('firstName'), minLength2Validator, noNumberValidator)}
								onChange={value => dispatch(setFirstName({ id: props.id, value }))}
							/>
						)}
					</div>
				)}
				{shows('phone', 'email') && (
					<div className='fieldLine'>
						{shows('phone') && (
							<LabelAndPhoneInput
								id={computeID(props.id, 'personPhone')}
								label='Téléphone'
								name='phone'
								placeholder='06xxxxxxxx'
								value={getPhone}
								onChange={value => dispatch(setPhone({ id: props.id, value }))}
								required={rules.requires('phone')}
							/>
						)}
						{shows('email') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personEmail')}
								label='Adresse mail'
								name='email'
								autocomplete='email'
								value={getEmail}
								placeholder='jean.dupont@gmail.com'
								validator={schemaValidator(rules.requires('email'), emailValidator)}
								onChange={value => dispatch(setEmail({ id: props.id, value }))}
							/>
						)}
					</div>
				)}
				{shows('birthDate', 'nationality') && (
					<div className='fieldLine'>
						{shows('birthDate') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personBirthDate')}
								type='date'
								name='birthDate'
								autocomplete='bday'
								label='Date de naissance'
								placeholder='jj/mm/aaaa'
								value={getBirthDate}
								validator={schemaValidator(rules.requires('birthDate'))}
								onChange={value => dispatch(setBirthDate({ id: props.id, value }))}
							/>
						)}
						{shows('nationality') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personNationality')}
								label='Nationalité'
								name='nationality'
								autocomplete='nationality'
								placeholder='Ex : Française'
								value={getNationality}
								validator={schemaValidator(rules.requires('nationality'), minLength2Validator, noNumberValidator)}
								onChange={value => dispatch(setNationality({ id: props.id, value }))}
							/>
						)}
					</div>
				)}
				{shows('birthCity', 'birthCountry') && (
					<div className='fieldLine'>
						{shows('birthCity') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personBirthCity')}
								label='Ville de naissance'
								name='city'
								autocomplete='address-level2'
								placeholder='Ex : Paris'
								value={getBirthCity}
								validator={schemaValidator(rules.requires('birthCity'), minLength2Validator, noNumberValidator)}
								onChange={value => dispatch(setBirthCity({ id: props.id, value }))}
							/>
						)}
						{shows('birthCountry') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personBirthCountry')}
								name='country'
								label='Pays de naissance'
								autocomplete='country'
								placeholder='Ex : France'
								value={getBirthCountry}
								validator={schemaValidator(rules.requires('birthCountry'), minLength2Validator, noNumberValidator)}
								onChange={value => dispatch(setBirthCountry({ id: props.id, value }))}
							/>
						)}
					</div>
				)}
				{shows('profession') && (
					<div className='fieldLine'>
						<LabelAndTextInput
							id={computeID(props.id, 'personProfession')}
							label='Profession'
							name='job'
							autocomplete='job'
							placeholder='Ex : artisan'
							value={getProfession}
							validator={schemaValidator(rules.requires('profession'), minLength2Validator, noNumberValidator)}
							onChange={value => dispatch(setProfession({ id: props.id, value }))}
						/>
					</div>
				)}
				{householdSource && rules.asks(...HOUSEHOLD_KEYS) && (
					<div className='sameHousehold'>
						<CheckAndLabel
							id={computeID(props.id, 'sameHousehold')}
							name='sameHousehold'
							text="Même adresse et situation familiale que l'acquéreur 1"
							checked={linked}
							required={false}
							onChange={setSameHousehold}
						/>
						{linked && <p className='householdSummary'>{householdSummary(householdSource)}</p>}
					</div>
				)}
				{!linked && shows('address') && (
					<div className='fieldLine'>
						<LabelAndTextInput
							id={computeID(props.id, 'personAddress')}
							label='Adresse'
							name='address'
							autocomplete='street-address'
							placeholder='Ex : 12 rue de Paris 75000 Paris'
							value={getAddress}
							validator={schemaValidator(rules.requires('address'), minLength2Validator)}
							onChange={value => dispatch(setAddress({ id: props.id, value }))}
						/>
					</div>
				)}
				{!linked && shows('maritalStatus', 'children') && (
					<div className='fieldLine'>
						{shows('maritalStatus') && (
							<SelectBoxAndLabel
								id={computeID(props.id, 'personMarried')}
								text='Status marital'
								options={MARITAL_STATUS_OPTIONS}
								value={getMarried}
								validator={schemaValidator(rules.requires('maritalStatus'))}
								onChange={value => dispatch(setMarried({ id: props.id, value }))}
							/>
						)}
						{shows('children') && (
							<LabelAndTextInput
								id={computeID(props.id, 'personChildren')}
								type='number'
								label='Nombre d’enfants'
								name='children'
								autocomplete='number'
								placeholder='Ex : 0'
								value={getChildren}
								validator={schemaValidator(rules.requires('children'), numberValidator)}
								onChange={value => dispatch(setChildren({ id: props.id, value }))}
							/>
						)}
					</div>
				)}
				{!linked && shows('marriageCity', 'marriageDate') && <Marriage id={props.id} rules={rules} answers={answers} />}
				{!linked && shows('hasContract') && (
						<div className='fieldLine'>
							<RadioGroupAndLabel
								id={computeID(props.id, 'personHasContract')}
								label='Avez-vous un contrat de mariage / PACS ?'
								options={{ no: 'Non', yes: 'Oui' }}
								checked={getHasContract}
								validator={schemaValidator(rules.requires('hasContract'))}
								onChange={value => dispatch(setHasContract({ id: props.id, value }))}
							/>
						</div>
					)}
			</div>
			{!linked && shows('contractDate', 'contractPlace', 'contractType') && (
					<Contract id={props.id} rules={rules} answers={answers} />
				)}
		</div>
	)
}

function Marriage(props: MarriageProps) {
	const dispatch = useAppDispatch()
	const { rules } = props
	const shows = (key: string) => rules.shows(key, props.answers)
	const getMarriageCity = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.marriageCity ?? '',
	)
	const getMarriageDate = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.marriageDate ?? '',
	)

	return (
		<div className='formContent' style={{ margin: '0' }}>
			<div className='fieldLine'>
				{shows('marriageCity') && (
					<LabelAndTextInput
						id={computeID(props.id, 'personMarriageCity')}
						label='Ville du mariage / PACS'
						name='city'
						placeholder='Ville'
						value={getMarriageCity}
						onChange={value => dispatch(setMarriageCity({ id: props.id, value }))}
						validator={schemaValidator(rules.requires('marriageCity'), minLength2Validator, noNumberValidator)}
						autocomplete='address-level2'
					/>
				)}
				{shows('marriageDate') && (
					<LabelAndTextInput
						id={computeID(props.id, 'personMarriageDate')}
						type='date'
						name='marriageDate'
						label='Date du mariage / PACS'
						placeholder='jj/mm/aaaa'
						value={getMarriageDate}
						onChange={value => dispatch(setMarriageDate({ id: props.id, value }))}
						validator={schemaValidator(rules.requires('marriageDate'))}
					/>
				)}
			</div>
		</div>
	)
}

function Contract(props: ContractProps) {
	const dispatch = useAppDispatch()
	const { rules } = props
	const shows = (key: string) => rules.shows(key, props.answers)
	const getContractDate = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.contractDate ?? '',
	)
	const getContractPlace = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.contractPlace ?? '',
	)
	const getContractType = useAppSelector(
		(state: RootState) =>
			state.person.persons.find(person => props.id === person.id)?.contractType ?? '',
	)

	return (
		<div className='formContent'>
			<div className='fieldLine'>
				{shows('contractDate') && (
					<LabelAndTextInput
						id={computeID(props.id, 'contractDate')}
						type='date'
						name='contractDate'
						label='Date du contrat'
						placeholder='jj/mm/aaaa'
						value={getContractDate}
						onChange={value => dispatch(setContractDate({ id: props.id, value }))}
						validator={schemaValidator(rules.requires('contractDate'))}
					/>
				)}
				{shows('contractPlace') && (
					<LabelAndTextInput
						id={computeID(props.id, 'contractPlace')}
						label='Lieu de signature'
						name='city'
						placeholder='Ville'
						value={getContractPlace}
						onChange={value => dispatch(setContractPlace({ id: props.id, value }))}
						validator={schemaValidator(rules.requires('contractPlace'), minLength2Validator, noNumberValidator)}
						autocomplete='address-level2'
					/>
				)}
				{shows('contractType') && (
					<SelectBoxAndLabel
						id={computeID(props.id, 'contractType')}
						text='Régime'
						options={{
							'': 'Sélectionner',
							universalCommunity: 'Communauté universelle',
							separated: 'Séparation de biens',
							participation: 'Participation aux acquêts',
							acquittalsCommunity: 'Communauté réduite aux acquêts',
						}}
						value={getContractType}
						validator={schemaValidator(rules.requires('contractType'))}
						onChange={value => dispatch(setContractType({ id: props.id, value }))}
					/>
				)}
			</div>
		</div>
	)
}

function Corporation(props: CorporationProps) {
	const dispatch = useAppDispatch()
	const { rules } = props

	const getCorporationName = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)?.name ?? '',
	)
	const getCorporationCapital = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)?.capital ?? '',
	)
	const getCorporationAddress = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)?.address ?? '',
	)
	const getCorporationSiren = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)?.siren ?? '',
	)
	const getCorporationRegistar = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)?.registar ?? '',
	)
	const getCorporationDelegateGender = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)
				?.delegateGender ?? '',
	)
	const getCorporationDelegateLastName = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)
				?.delegateLastName ?? '',
	)
	const getCorporationDelegateFirstName = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)
				?.delegateFirstName ?? '',
	)
	const getCorporationDelegatePhone = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)
				?.delegatePhone ?? '',
	)
	const getCorporationDelegateEmail = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)
				?.delegateEmail ?? '',
	)
	const getCorporationDelegateAddress = useAppSelector(
		(state: RootState) =>
			state.corporation.corporations.find(corporation => props.id === corporation.id)
				?.delegateAddress ?? '',
	)

	// This corporation's answers, for the schema's conditions on its questions.
	const answers = {
		...props.answers,
		corporationName: getCorporationName,
		corporationCapital: getCorporationCapital,
		corporationAddress: getCorporationAddress,
		corporationSiren: getCorporationSiren,
		corporationRegistar: getCorporationRegistar,
		delegateGender: getCorporationDelegateGender,
		delegateLastName: getCorporationDelegateLastName,
		delegateFirstName: getCorporationDelegateFirstName,
		delegatePhone: getCorporationDelegatePhone,
		delegateEmail: getCorporationDelegateEmail,
		delegateAddress: getCorporationDelegateAddress,
	}
	// Whether the schema shows any of these questions here: for the answers so
	// far, and to this corporation, as some are only asked of the first.
	const shows = (...keys: string[]) =>
		keys.some(key => rules.shows(key, answers) && (props.position === 0 || !rules.isFirstBuyerOnly(key)))

	return (
		<div className='formContent'>
			{shows(
					'corporationName',
					'corporationCapital',
					'corporationAddress',
					'corporationSiren',
					'corporationRegistar',
				) && (
					<>
						{shows('corporationName', 'corporationCapital') && (
							<div className='fieldLine'>
								{shows('corporationName') && (
									<LabelAndTextInput
										id={computeID(props.id, 'corporationName')}
										label='Raison sociale'
										name='corporationName'
										autocomplete='organization'
										placeholder="Ex : SAS de l'Étoile"
										value={getCorporationName}
										onChange={value => dispatch(setCorporationName({ id: props.id, value }))}
										validator={schemaValidator(rules.requires('corporationName'), minLength2Validator, noNumberValidator)}
									/>
								)}
								{shows('corporationCapital') && (
									<LabelAndTextInput
										id={computeID(props.id, 'corporationCapital')}
										label='Capital social'
										name='corporationCapital'
										placeholder='Ex : 1 500€'
										value={getCorporationCapital}
										onChange={value => dispatch(setCorporationCapital({ id: props.id, value }))}
										validator={schemaValidator(rules.requires('corporationCapital'), numberValidator)}
										autocomplete='on'
									/>
								)}
							</div>
						)}
						{shows('corporationAddress') && (
							<div className='fieldLine'>
								<LabelAndTextInput
									id={computeID(props.id, 'corporationAddress')}
									name='address'
									autocomplete='street-address'
									label='Adresse de la société'
									placeholder='Ex : chemin de l’Etoile 06000 Nice'
									value={getCorporationAddress}
									onChange={value => dispatch(setCorporationAddress({ id: props.id, value }))}
									validator={schemaValidator(rules.requires('corporationAddress'), minLength2Validator)}
								/>
							</div>
						)}
						{shows('corporationSiren', 'corporationRegistar') && (
							<div className='fieldLine'>
								{shows('corporationSiren') && (
									<LabelAndTextInput
										id={computeID(props.id, 'corporationSiren')}
										label='SIREN'
										name='siren'
										placeholder='Ex : 123456789'
										value={getCorporationSiren}
										onChange={value => dispatch(setCorporationSiren({ id: props.id, value }))}
										validator={schemaValidator(rules.requires('corporationSiren'), numberValidator)}
										autocomplete='on'
									/>
								)}
								{shows('corporationRegistar') && (
									<LabelAndTextInput
										id={computeID(props.id, 'corporationRegistar')}
										label='Ville où a été enregistrée la société'
										name='city'
										placeholder='Ex : Nice'
										value={getCorporationRegistar}
										onChange={value => dispatch(setCorporationRegistar({ id: props.id, value }))}
										validator={schemaValidator(rules.requires('corporationRegistar'), minLength2Validator, noNumberValidator)}
										autocomplete='address-level2'
									/>
								)}
							</div>
						)}
					</>
				)}
			<div className='underSectionSeparator'>Représentant de la société</div>
			{shows('delegateGender') && (
				<div className='fitContentLine'>
					<SelectBoxAndLabel
						id={computeID(props.id, 'corporationDelegateGender')}
						text='Civilité'
						options={{ '': 'Sélectionner', mr: 'M', mrs: 'Mme' }}
						value={getCorporationDelegateGender}
						validator={schemaValidator(rules.requires('delegateGender'))}
						onChange={value => dispatch(setCorporationDelegateGender({ id: props.id, value }))}
					/>
				</div>
			)}
			{shows('delegateLastName', 'delegateFirstName') && (
				<div className='fieldLine'>
					{shows('delegateLastName') && (
						<LabelAndTextInput
							id={computeID(props.id, 'corporationDelegateLastname')}
							label='Nom'
							name='lastName'
							placeholder='Ex : Dupont'
							value={getCorporationDelegateLastName}
							onChange={value => dispatch(setCorporationDelegateLastName({ id: props.id, value }))}
							validator={schemaValidator(rules.requires('delegateLastName'), minLength2Validator, noNumberValidator)}
							autocomplete='family-name'
						/>
					)}
					{shows('delegateFirstName') && (
						<LabelAndTextInput
							id={computeID(props.id, 'corporationDelegateFirstname')}
							label='Prénom'
							name='firstName'
							placeholder='Ex : Jean'
							value={getCorporationDelegateFirstName}
							onChange={value => dispatch(setCorporationDelegateFirstName({ id: props.id, value }))}
							validator={schemaValidator(rules.requires('delegateFirstName'), minLength2Validator, noNumberValidator)}
							autocomplete='given-name'
						/>
					)}
				</div>
			)}
			{shows('delegatePhone', 'delegateEmail') && (
				<div className='fieldLine'>
					{shows('delegatePhone') && (
						<LabelAndPhoneInput
							id={computeID(props.id, 'corporationDelegatePhone')}
							label='Téléphone'
							name='phone'
							placeholder='06xxxxxxxx'
							value={getCorporationDelegatePhone}
							onChange={value => dispatch(setCorporationDelegatePhone({ id: props.id, value }))}
							required={rules.requires('delegatePhone')}
						/>
					)}
					{shows('delegateEmail') && (
						<LabelAndTextInput
							id={computeID(props.id, 'corporationDelegateEmail')}
							label='Adresse mail'
							name='email'
							placeholder='Ex : jean.dupont@leficaneur.com'
							value={getCorporationDelegateEmail}
							onChange={value => dispatch(setCorporationDelegateEmail({ id: props.id, value }))}
							validator={schemaValidator(rules.requires('delegateEmail'), emailValidator)}
							autocomplete='email'
						/>
					)}
				</div>
			)}
			{shows('delegateAddress') && (
				<div className='fieldLine'>
					<LabelAndTextInput
						id={computeID(props.id, 'corporationDelegateAddress')}
						label='Adresse'
						name='address'
						placeholder='Ex : 12 rue de Paris 75000 Paris'
						value={getCorporationDelegateAddress}
						onChange={value => dispatch(setCorporationDelegateAddress({ id: props.id, value }))}
						validator={schemaValidator(rules.requires('delegateAddress'), minLength2Validator)}
						autocomplete='street-address'
					/>
				</div>
			)}
		</div>
	)
}

export default Section2
